// useCodeNeighbourPrices — what one billing code (CPT / HCPCS / MS-DRG / REV)
// costs at this hospital and at its neighbours, for the City view's map
// filter. Source: hospital_procedure_analytics (one row per hospital ×
// code × setting/line; we take the per-hospital MEDIAN of its listed
// charges so a single dosage line can't swing the marker). The code's
// national median / description come from the static codes/<fam>/<code>
// shard. The shard's own hospital list is the 500 cheapest + 500 most
// expensive nationally — useless for a neighbourhood — so we don't use it
// for prices.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { loadStaticData } from "@/lib/offlineData";

export type CodeFamily = "cpt" | "hcpcs" | "drg" | "rev";
export const FAMILY_LABEL: Record<CodeFamily, string> = { cpt: "CPT", hcpcs: "HCPCS", drg: "MS-DRG", rev: "REV" };
const HPA_TYPE: Record<CodeFamily, string> = { cpt: "cpt", hcpcs: "hcpcs", drg: "drg", rev: "revenue" };
// PostgREST caps a response at 1,000 rows; busy codes carry ~30 lines per
// hospital, so keep chunks small. 300 nearest hospitals = 12 requests.
const CHUNK = 25;
const MAX_IDS = 300;

/** hospital_procedure_analytics stores DRGs bare and zero-padded ('001', '470'). */
export function hpaCode(fam: CodeFamily, code: string): string {
  if (fam !== "drg") return code.trim();
  const bare = code.trim().replace(/^MS-/i, "").replace(/^0+/, "") || "0";
  return bare.padStart(3, "0");
}
/** Static shard files: codes/drg/MS-470.json, codes/cpt/99213.json, codes/rev/0450.json. */
export function shardCode(fam: CodeFamily, code: string): string {
  return fam === "drg" ? `MS-${hpaCode(fam, code)}` : code.trim();
}

interface CodeShardFile {
  data?: { description?: string; consumerName?: string; pricing?: { medianGrossCharge?: number | null; avgGrossCharge?: number | null; totalHospitals?: number | null } };
}

export interface CodeNeighbourPrices {
  prices: Map<string, number>;   // neighbour id → median listed charge
  here: number | null;           // this hospital, same rule
  nationalMedian: number | null;
  nationalAvg: number | null;
  totalHospitals: number | null;
  description: string | null;
  consumerName: string | null;
  truncated: boolean;            // a chunk hit the 1,000-row cap
}

const median = (a: number[]) => { const s = [...a].sort((x, y) => x - y); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

export function useCodeNeighbourPrices(fam: CodeFamily | null, code: string | null, hospitalId: string, neighbourIds: string[]) {
  const ids = neighbourIds.slice(0, MAX_IDS);
  const key = ids.join("|");
  return useQuery<CodeNeighbourPrices>({
    queryKey: ["code-neighbour-prices", fam, code, hospitalId, key],
    enabled: !!fam && !!code && !!hospitalId,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const pcode = hpaCode(fam!, code!);
      const all = [hospitalId, ...ids];
      const perHospital = new Map<string, number[]>();
      let truncated = false;
      const chunks: string[][] = [];
      for (let i = 0; i < all.length; i += CHUNK) chunks.push(all.slice(i, i + CHUNK));
      const sb: any = supabase;
      await Promise.all(chunks.map(async (chunk) => {
        const { data, error } = await sb
          .from("hospital_procedure_analytics")
          .select("hospital_id, hospital_gross_charge")
          .eq("procedure_code", pcode)
          .eq("procedure_type", HPA_TYPE[fam!])
          .in("hospital_id", chunk)
          .gte("hospital_gross_charge", 2)
          .limit(1000);
        if (error) throw error;
        const rows = (data || []) as { hospital_id: string; hospital_gross_charge: number | string }[];
        if (rows.length >= 1000) truncated = true;
        for (const r of rows) {
          const v = Number(r.hospital_gross_charge);
          if (!Number.isFinite(v)) continue;
          const arr = perHospital.get(r.hospital_id) || [];
          arr.push(v);
          perHospital.set(r.hospital_id, arr);
        }
      }));
      const prices = new Map<string, number>();
      for (const [id, arr] of perHospital) prices.set(id, median(arr));
      const here = prices.get(hospitalId) ?? null;
      prices.delete(hospitalId);
      let shard: CodeShardFile | null = null;
      try { shard = await loadStaticData<CodeShardFile>(`codes/${fam}/${encodeURIComponent(shardCode(fam!, code!))}.json`); } catch { shard = null; }
      const p = shard?.data?.pricing;
      return {
        prices, here,
        nationalMedian: p?.medianGrossCharge ?? null, nationalAvg: p?.avgGrossCharge ?? null, totalHospitals: p?.totalHospitals ?? null,
        description: shard?.data?.description ?? null, consumerName: shard?.data?.consumerName || null, truncated,
      };
    },
  });
}
